var _RemoveCircle, _AddCircle, _Separator;
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useCallback } from 'react';
import { arrayOf, number, oneOfType, shape, string, node, func } from 'prop-types';
import clsx from 'clsx';
import { styled } from '@mui/material/styles';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { AddCircle, RemoveCircle } from 'mosaic-react-icons';
import SovosTypography from '../sovos-typography';
import OrderableListItem from './components/OrderableListItem';
import useMosaicTranslation from '../internals/i18n/useMosaicTranslation';
const listStyles = {
  listStyle: 'none',
  margin: 0,
  padding: 0
};
const Root = styled('section')(_ref => {
  let {
    theme: {
      palette
    }
  } = _ref;
  return {
    background: palette.common.white
  };
});
const Header = styled('header')(_ref2 => {
  let {
    theme: {
      spacing
    }
  } = _ref2;
  return {
    padding: "0 0 ".concat(spacing(2), " 0")
  };
});
const ActiveList = styled('ol')(listStyles);
const InactiveList = styled('ul')(listStyles);
const Separator = styled('hr')(_ref3 => {
  let {
    theme: {
      palette,
      spacing
    }
  } = _ref3;
  return {
    border: 0,
    borderBottom: "1px solid ".concat(palette.divider),
    height: 0,
    margin: "".concat(spacing(3), " 0")
  };
});
const SovosOrderableList = _ref4 => {
  let {
    'data-testid': dataTestId,
    activeIcon = _RemoveCircle || (_RemoveCircle = /*#__PURE__*/React.createElement(RemoveCircle, null)),
    activeItems = [],
    className,
    inactiveIcon = _AddCircle || (_AddCircle = /*#__PURE__*/React.createElement(AddCircle, null)),
    inactiveItems,
    labels,
    minActiveItems = 0,
    onChange,
    ...rest
  } = _ref4;
  const {
    t
  } = useMosaicTranslation();
  const activeList = activeItems;
  const inactiveList = inactiveItems;
  const disableRemove = minActiveItems >= activeList.length;
  const updateItem = (index, active) => {
    const itemCopy = active ? activeList[index] : inactiveList[index];
    if (active) {
      const inactiveListCopy = [...inactiveList, itemCopy];
      const activeListCopy = activeList.filter(item => item !== itemCopy);
      onChange(activeListCopy, inactiveListCopy);
    } else {
      const activeListCopy = [...activeList, itemCopy];
      const inactiveListCopy = inactiveList.filter(item => item !== itemCopy);
      onChange(activeListCopy, inactiveListCopy);
    }
  };
  const moveItem = useCallback((dragIndex, hoverIndex) => {
    const clonedList = activeList.map(item => ({
      ...item
    }));
    const dragItem = clonedList[dragIndex];
    clonedList.splice(dragIndex, 1);
    clonedList.splice(hoverIndex, 0, dragItem);
    onChange(clonedList, inactiveList);
  }, [activeList, inactiveList, onChange]);
  const renderItems = (listItems, active) => listItems.map((item, index) => /*#__PURE__*/React.createElement(OrderableListItem, {
    active: active,
    activeIcon: activeIcon,
    disableRemove: disableRemove && active,
    id: item.id,
    inactiveIcon: inactiveIcon,
    index: index,
    key: item.id,
    labels: {
      addTooltipText: (labels === null || labels === void 0 ? void 0 : labels.addTooltipText) || t('orderableList.addTooltip'),
      removeDisabledTooltipText: (labels === null || labels === void 0 ? void 0 : labels.removeDisabledTooltipText) || t('orderableList.removeDisabledTooltipText'),
      removeTooltipText: (labels === null || labels === void 0 ? void 0 : labels.removeTooltipText) || t('orderableList.removeTooltip')
    },
    moveItem: moveItem,
    title: item.title,
    updateItem: updateItem
  }));
  return /*#__PURE__*/React.createElement(DndProvider, {
    backend: HTML5Backend
  }, /*#__PURE__*/React.createElement(Root, _extends({
    className: clsx('sovosOrderableList', className),
    "data-testid": dataTestId
  }, rest), /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(SovosTypography, {
    align: "left",
    color: "text.secondary",
    variant: "body2"
  }, (labels === null || labels === void 0 ? void 0 : labels.header) || t('orderableList.header'))), activeList.length > 0 ? /*#__PURE__*/React.createElement(ActiveList, null, renderItems(activeList, true)) : null, activeList.length > 0 && inactiveList.length > 0 ? _Separator || (_Separator = /*#__PURE__*/React.createElement(Separator, null)) : null, inactiveList.length > 0 ? /*#__PURE__*/React.createElement(InactiveList, null, renderItems(inactiveList, false)) : null));
};
SovosOrderableList.propTypes = {
  /**
   * ActiveIcon component
   */
  activeIcon: node,
  /**
   * Array of active list items
   */
  activeItems: arrayOf(shape({
    id: oneOfType([string, number]).isRequired,
    title: string.isRequired
  })),
  /**
   * Extend the class name applied to the root element
   */
  className: string,
  /**
   * @ignore
   */
  'data-testid': string,
  /**
   * InactiveIcon component
   */
  inactiveIcon: node,
  /**
   * Array of inactive list items
   */
  inactiveItems: arrayOf(shape({
    id: oneOfType([string, number]).isRequired,
    title: string.isRequired
  })).isRequired,
  /**
   * Object of the following properties: addTooltipText, header,
   * removeDisabledTooltipText, and removeTooltipText
   */
  labels: shape({
    addTooltipText: node,
    header: string,
    removeDisabledTooltipText: node,
    removeTooltipText: node
  }),
  /**
   * Minimun active items defaults to zero
   */
  minActiveItems: number,
  /**
   * Callback fired when the state of the list changes
   * `function(newActiveItems: object[], newInactiveItems: object[]) => void`
   */
  onChange: func.isRequired
};
export default SovosOrderableList;